// Single source of truth for the per-user favicon worker.
//
// The deployable worker code (FAVICON_WORKER_SOURCE) is BUILT from fn.toString()
// of the functions below, so the code we deploy is the exact code we unit-test —
// no drift — and the string contains no export/import (it must run standalone on
// Puter). Constraints that keep this robust under production minification:
//   * worker functions reference NO module-scope bindings (only each other by
//     name + worker-runtime globals fetch/btoa/Response/URL/Uint8Array);
//   * the entry handler is registered via handleIcon.name, never a literal name.
// Minification renames the functions consistently within this module, and the
// names we emit come from the functions themselves, so references always resolve.

export function sanitizeHost(h) {
  if (typeof h !== 'string') return null;
  const t = h.trim().toLowerCase();
  if (!/^[a-z0-9.-]{1,253}$/.test(t)) return null;
  if (t.includes('..') || t.startsWith('.') || t.endsWith('.')) return null;
  if (!t.includes('.')) return null;
  const m = t.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (m) {
    const a = Number(m[1]); const b = Number(m[2]);
    if (a === 0 || a === 10 || a === 127 ||
        (a === 169 && b === 254) || (a === 192 && b === 168) ||
        (a === 172 && b >= 16 && b <= 31)) return null;
  }
  return t;
}

export function base64(bytes) {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

async function tryFetchIcon(target) {
  const MAX_BYTES = 50 * 1024;
  const MAX_HOPS = 3;
  let res;
  let url = target;
  // Follow redirects by hand so every hop is re-checked against sanitizeHost.
  // With redirect:'follow' the guard only ever saw the FIRST host: an
  // attacker-controlled site could answer /favicon.ico with a 302 to
  // http://169.254.169.254/… and the worker would dutifully issue that
  // server-side request to the very internal address the guard exists to block.
  for (let hop = 0; ; hop++) {
    try { res = await fetch(url, { redirect: 'manual' }); } catch { return null; }
    if (res.status < 300 || res.status > 399) break;
    if (hop >= MAX_HOPS) return null;
    const loc = res.headers.get('location');
    if (!loc) return null;
    let next;
    try { next = new URL(loc, url); } catch { return null; }
    if (next.protocol !== 'https:' && next.protocol !== 'http:') return null;
    if (!sanitizeHost(next.hostname)) return null;
    url = next.toString();
  }
  if (!res.ok) return null;
  const ct = (res.headers.get('content-type') || '').toLowerCase();
  if (!ct.startsWith('image/')) return null;
  const bytes = new Uint8Array(await res.arrayBuffer());
  if (bytes.length === 0 || bytes.length > MAX_BYTES) return null;
  return 'data:' + ct.split(';')[0] + ';base64,' + base64(bytes);
}

async function handleIcon({ request }) {
  const CORS = {
    'Access-Control-Allow-Origin': '*',
    'Access-Control-Allow-Methods': 'GET, OPTIONS',
    'Access-Control-Allow-Headers': '*',
  };
  const json = (obj) => new Response(JSON.stringify(obj), {
    status: 200,
    headers: { 'Content-Type': 'application/json', ...CORS },
  });
  const host = sanitizeHost(new URL(request.url).searchParams.get('host'));
  if (!host) return json({ dataUrl: null });
  const candidates = ['https://' + host + '/favicon.ico',
                      'https://icons.duckduckgo.com/ip3/' + host + '.ico'];
  for (const target of candidates) {
    const dataUrl = await tryFetchIcon(target);
    if (dataUrl) return json({ dataUrl });
  }
  return json({ dataUrl: null });
}

// Bumped to 2 when redirect handling moved from 'follow' to per-hop
// revalidation; already-deployed v1 workers must be replaced.
export const FAVICON_WORKER_VERSION = 2;

export const FAVICON_WORKER_SOURCE = [
  sanitizeHost.toString(),
  base64.toString(),
  tryFetchIcon.toString(),
  handleIcon.toString(),
  "router.options('/icon', async () => new Response(null, { status: 204, headers: { 'Access-Control-Allow-Origin': '*', 'Access-Control-Allow-Methods': 'GET, OPTIONS', 'Access-Control-Allow-Headers': '*' } }));",
  "router.get('/icon', " + handleIcon.name + ");",
].join('\n\n');
